import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Panel, StatusPill, money } from "../components/lux";
import { ArrowLeft, DownloadSimple, Warning, CheckCircle, MagnifyingGlass, Prohibit } from "@phosphor-icons/react";

const drvLabel = (d) => `${d.first_name || ""} ${d.last_name || ""}`.trim() || "Unnamed";
const pretty = (k) => k.replace(/^\d+_/, "").replace(/_/g, " ");

function CauseRow({ ck, cv }) {
  const scalars = Object.entries(cv).filter(([k, v]) => k !== "exists" && k !== "note" && k !== "where" && (typeof v === "number" || typeof v === "string"));
  return (
    <div className="px-4 py-3 border-b border-border/40" data-testid={`cause-${ck}`}>
      <div className="flex items-center gap-2">
        {cv.exists ? <Warning size={15} className="text-amber-400" weight="fill" /> : <CheckCircle size={15} className="text-emerald-400" weight="fill" />}
        <span className="font-heading capitalize">{pretty(ck)}</span>
        <span className={`label-mono text-[10px] ${cv.exists ? "text-amber-400" : "text-emerald-400"}`}>{cv.exists ? "present" : "none"}</span>
      </div>
      {cv.where && <div className="text-xs text-muted-foreground mt-1">{Array.isArray(cv.where) ? cv.where.join(" · ") : cv.where}</div>}
      {scalars.length > 0 && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1.5 text-xs">
          {scalars.map(([k, v]) => <span key={k}><span className="text-muted-foreground">{pretty(k)}:</span> <span className="tnum font-medium">{String(v)}</span></span>)}
        </div>
      )}
      {cv.values && Object.keys(cv.values).length > 0 && <div className="text-xs mt-1">values: {Object.entries(cv.values).map(([k, v]) => `${k} (${v})`).join(", ")}</div>}
      {cv.counts && <div className="text-xs mt-1">{Object.entries(cv.counts).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>}
      {cv.note && <div className="text-[11px] text-muted-foreground mt-1 italic">{cv.note}</div>}
    </div>
  );
}

function LinkGroups({ title, kind, groups, records, labelFn, mapState, setMapState, testid }) {
  const buckets = [
    { key: "exact", label: "Exact match", icon: CheckCircle, tone: "text-emerald-400", hint: "Ticked by default — applies on Apply selected." },
    { key: "fuzzy", label: "Fuzzy suggestion", icon: MagnifyingGlass, tone: "text-amber-400", hint: "Unticked — review before ticking." },
    { key: "unresolved", label: "Unresolved", icon: Prohibit, tone: "text-muted-foreground", hint: "No match or more than one — map manually." },
  ];
  const keyFor = (name) => `${kind}::${name}`;
  const upd = (name, patch) => setMapState((m) => ({ ...m, [keyFor(name)]: { ...m[keyFor(name)], ...patch } }));
  return (
    <div className="space-y-4" data-testid={testid}>
      {buckets.map((b) => {
        const list = groups[b.key] || [];
        if (list.length === 0) return null;
        return (
          <div key={b.key}>
            <div className="flex items-center gap-2 mb-1.5"><b.icon size={14} className={b.tone} /><span className="label-mono">{b.label} ({list.length})</span><span className="text-[11px] text-muted-foreground">{b.hint}</span></div>
            <div className="border border-border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left">
                  <th className="px-3 py-2 label-mono font-normal w-10"></th>
                  <th className="px-3 py-2 label-mono font-normal">Stored name</th>
                  <th className="px-3 py-2 label-mono font-normal w-20 text-right">Bookings</th>
                  <th className="px-3 py-2 label-mono font-normal">Map to record</th>
                </tr></thead>
                <tbody>
                  {list.map((g) => {
                    const st = mapState[keyFor(g.name)] || {};
                    return (
                      <tr key={g.name} className="border-b border-border/40" data-testid={`${testid}-row`}>
                        <td className="px-3 py-2"><input type="checkbox" checked={!!st.checked} onChange={(e) => upd(g.name, { checked: e.target.checked })} data-testid={`${kind}-check-${g.name}`} /></td>
                        <td className="px-3 py-2 font-heading">{g.name}{g.revenue != null ? <span className="text-[11px] text-muted-foreground ml-2">{money(g.revenue)}</span> : null}</td>
                        <td className="px-3 py-2 text-right tnum">{g.count}</td>
                        <td className="px-3 py-2">
                          <select value={st.target_id || ""} onChange={(e) => upd(g.name, { target_id: e.target.value, checked: !!e.target.value })}
                            className="bg-input border border-border rounded-xl px-2 py-1.5 text-sm w-full max-w-xs" data-testid={`${kind}-map-${g.name}`}>
                            <option value="">— choose a record —</option>
                            {records.map((r) => <option key={r.id} value={r.id}>{labelFn(r)}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      {(groups.exact.length + groups.fuzzy.length + groups.unresolved.length) === 0 && <div className="text-sm text-muted-foreground px-1">Nothing to link.</div>}
    </div>
  );
}

export default function BookingHistoryDiagnostics() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [salespeople, setSalespeople] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [migrations, setMigrations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [spMap, setSpMap] = useState({});
  const [drvMap, setDrvMap] = useState({});

  const initMaps = useCallback((rep) => {
    const sp = {};
    (rep.salesperson_links.exact || []).forEach((g) => { sp[`salesperson::${g.name}`] = { checked: true, target_id: g.candidates[0]?.id || "" }; });
    (rep.salesperson_links.fuzzy || []).forEach((g) => { sp[`salesperson::${g.name}`] = { checked: false, target_id: g.candidates[0]?.id || "" }; });
    (rep.salesperson_links.unresolved || []).forEach((g) => { sp[`salesperson::${g.name}`] = { checked: false, target_id: "" }; });
    const dv = {};
    (rep.driver_links.exact || []).forEach((g) => { dv[`driver::${g.name}`] = { checked: true, target_id: g.candidates[0]?.id || "" }; });
    (rep.driver_links.fuzzy || []).forEach((g) => { dv[`driver::${g.name}`] = { checked: false, target_id: g.candidates[0]?.id || "" }; });
    (rep.driver_links.unresolved || []).forEach((g) => { dv[`driver::${g.name}`] = { checked: false, target_id: "" }; });
    setSpMap(sp); setDrvMap(dv);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rep, sps, drv, mig] = await Promise.all([
        api.get("/admin/booking-history/report"),
        api.get("/salespeople").catch(() => ({ data: [] })),
        api.get("/chauffeurs").catch(() => ({ data: [] })),
        api.get("/admin/migration-reports").catch(() => ({ data: { reports: [], backups: [] } })),
      ]);
      setReport(rep.data);
      setSalespeople(sps.data || []);
      setDrivers(drv.data || []);
      setMigrations(mig.data);
      initMaps(rep.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [initMaps]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/booking-history/report.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "booking-history-diagnostics.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const selectedCount = useMemo(() => {
    const sc = Object.values(spMap).filter((v) => v.checked && v.target_id).length;
    const dc = Object.values(drvMap).filter((v) => v.checked && v.target_id).length;
    return sc + dc;
  }, [spMap, drvMap]);

  const applySelected = async () => {
    const salesperson = Object.entries(spMap).filter(([, v]) => v.checked && v.target_id).map(([k, v]) => ({ name: k.slice("salesperson::".length), target_id: v.target_id }));
    const driver = Object.entries(drvMap).filter(([, v]) => v.checked && v.target_id).map(([k, v]) => ({ name: k.slice("driver::".length), target_id: v.target_id }));
    if (salesperson.length + driver.length === 0) { toast.error("Nothing ticked to apply"); return; }
    if (!window.confirm(`Apply ${salesperson.length} salesperson and ${driver.length} driver link(s)? This writes to the database and saves each mapped name as an alias.`)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/admin/booking-history/apply-links", { salesperson, driver, save_alias: true });
      const spn = data.salesperson.reduce((s, x) => s + x.bookings_linked, 0);
      const dvn = data.driver.reduce((s, x) => s + x.trips_linked, 0);
      toast.success(`Linked ${spn} booking(s) to salespeople and ${dvn} trip(s) to drivers.${data.skipped.length ? ` ${data.skipped.length} skipped.` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  if (user && user.role !== "admin") return <Navigate to="/settings" replace />;

  return (
    <div className="space-y-6" data-testid="booking-history-diagnostics-page">
      <div>
        <Link to="/settings" className="label-mono inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-settings">
          <ArrowLeft size={14} /> Settings
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3 mt-2">
          <div>
            <h1 className="font-heading text-3xl">Booking history diagnostics</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">Why bookings drop out of a login&apos;s personal view, and a dry run of the salesperson / driver link repair. This runs live against the database you are signed in to and <span className="text-foreground">writes nothing until you press Apply selected</span>.</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" onClick={downloadCsv} data-testid="download-report-btn"><DownloadSimple size={14} className="mr-1.5" />Download report</Button>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90" disabled={applying || selectedCount === 0} onClick={applySelected} data-testid="apply-selected-btn">
              {applying ? "Applying…" : `Apply selected (${selectedCount})`}
            </Button>
          </div>
        </div>
      </div>

      {loading || !report ? <div className="p-12 text-center text-muted-foreground border border-border bg-card rounded-2xl">Computing report…</div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="bh-totals">
            {Object.entries(report.totals).map(([k, v]) => (
              <div key={k} className="border border-border bg-card rounded-2xl p-4">
                <div className="label-mono text-[10px]">{pretty(k)}</div>
                <div className="font-heading text-2xl tnum mt-1">{v}</div>
              </div>
            ))}
          </div>

          {migrations && (migrations.reports?.length > 0 || migrations.backups?.length > 0) && (
            <Panel label="Boot health" title="Startup migrations &amp; backups" testid="migrations-panel" flush>
              <div className="px-4 py-3 border-b border-border/40">
                <div className="label-mono text-[10px] mb-2">Migrations (last run)</div>
                <div className="flex flex-wrap gap-2">
                  {(migrations.reports || []).map((r) => {
                    const rep = r.report || {};
                    const bits = ["updated", "repaired", "rows_created", "deals", "needs_review", "skipped"].filter((k) => rep[k] != null && rep[k] !== 0).map((k) => `${pretty(k)} ${rep[k]}`);
                    return (
                      <span key={r.id || r.name} className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1 text-xs ${r.ok ? "border-emerald-500/40" : "border-red-500/50"}`} data-testid={`migration-${r.name}`}>
                        {r.ok ? <CheckCircle size={13} className="text-emerald-400" weight="fill" /> : <Warning size={13} className="text-red-400" weight="fill" />}
                        <span className="font-medium">{pretty(r.name)}</span>
                        {bits.length > 0 && <span className="text-muted-foreground">· {bits.join(" · ")}</span>}
                        {!r.ok && <span className="text-red-400">· {r.error || "failed"}</span>}
                      </span>
                    );
                  })}
                </div>
              </div>
              {migrations.backups?.length > 0 && (
                <div className="px-4 py-3">
                  <div className="label-mono text-[10px] mb-1.5">Pre-migration backups on this database</div>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    {migrations.backups.map((b) => <div key={b.backup}>{b.collection} → <span className="text-foreground">{b.backup}</span> ({b.count} docs)</div>)}
                  </div>
                </div>
              )}
            </Panel>
          )}

          <Panel label="§2 Diagnosis" title="Why bookings drop" testid="causes-panel" flush>
            {Object.entries(report.causes).map(([ck, cv]) => <CauseRow key={ck} ck={ck} cv={cv} />)}
          </Panel>

          <Panel label="§7 Dry run" title="Salesperson links" testid="sp-links-panel">
            <LinkGroups title="Salesperson" kind="salesperson" groups={report.salesperson_links} records={salespeople}
              labelFn={(r) => r.name} mapState={spMap} setMapState={setSpMap} testid="sp-links" />
            {report.salesperson_links.empty_count > 0 && <div className="text-xs text-muted-foreground mt-3">{report.salesperson_links.empty_count} booking(s) have no salesperson at all (see Settings → Unlinked bookings once phase 3 ships).</div>}
          </Panel>

          <Panel label="§7 Dry run" title="Driver links (name-only trips)" testid="drv-links-panel">
            <LinkGroups title="Driver" kind="driver" groups={report.driver_links} records={drivers}
              labelFn={drvLabel} mapState={drvMap} setMapState={setDrvMap} testid="drv-links" />
          </Panel>

          {report.impact.drivers.length > 0 && (
            <Panel label="Impact" title="Per driver — Drivers tab after linking" testid="drv-impact-panel">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-border text-left">
                    {["Driver", "Hours (before → after)", "Total made (before → after)", "Owed (before → after)", "Trips added"].map((h) => <th key={h} className="px-4 py-2 label-mono font-normal">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {report.impact.drivers.map((d) => (
                      <tr key={d.id} className="border-b border-border/40">
                        <td className="px-4 py-2 font-heading">{d.name}</td>
                        <td className="px-4 py-2 tnum">{d.before.hours} → <span className={d.after.hours !== d.before.hours ? "text-amber-400" : ""}>{d.after.hours}</span></td>
                        <td className="px-4 py-2 tnum">{money(d.before.made)} → <span className={d.after.made !== d.before.made ? "text-amber-400" : ""}>{money(d.after.made)}</span></td>
                        <td className="px-4 py-2 tnum">{money(d.before.owed)} → <span className={d.after.owed !== d.before.owed ? "text-amber-400" : ""}>{money(d.after.owed)}</span></td>
                        <td className="px-4 py-2 tnum">{d.would_add}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2 italic">Linking an old completed trip to a driver can make Owed rise for trips already paid outside the CRM. Review before applying.</p>
            </Panel>
          )}

          {report.impact.salespeople.length > 0 && (
            <Panel label="Impact" title="Per salesperson — Sales Performance after linking" testid="sp-impact-panel">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-border text-left">
                    {["Salesperson", "Bookings (before → after)", "Revenue (before → after)", "Over-match"].map((h) => <th key={h} className="px-4 py-2 label-mono font-normal">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {report.impact.salespeople.map((s) => (
                      <tr key={s.id} className="border-b border-border/40">
                        <td className="px-4 py-2 font-heading">{s.name}</td>
                        <td className="px-4 py-2 tnum">{s.before_count} → <span className={s.after_count !== s.before_count ? "text-amber-400" : ""}>{s.after_count}</span></td>
                        <td className="px-4 py-2 tnum">{money(s.before_revenue)} → <span className={s.after_revenue !== s.before_revenue ? "text-amber-400" : ""}>{money(s.after_revenue)}</span></td>
                        <td className={`px-4 py-2 tnum ${s.overmatch ? "text-amber-400" : "text-muted-foreground"}`}>{s.overmatch}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2 italic">Over-match = bookings the current name regex shows this rep that actually belong to someone else. Switching My Bookings to record ids (phase 3) brings this to zero.</p>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}
